import { Group, NumberInput, Stack, Text } from '@mantine/core';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TravelRequestSchema } from '@/module/Travel/Request/model/TravelRequestSchema';
import {
  AbsenceMotivation,
  TravelActivityType,
  TravelDayTotals,
  travelCapDaysBlockSubmit,
  travelDaySum,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { NumberInputControl } from '@/core/components';

import { travelFormationActivityTypes } from '../TravelDayBalanceCommon';

interface TravelAbsenceDayFieldsProps {
  control: Control<TravelRequestSchema>;
  isSubmitting?: boolean;
  activityType: TravelActivityType;
  absenceMotivation: AbsenceMotivation;
  totals: TravelDayTotals | undefined;
}

// The beyond-cap motivation charges its uncovered days to the pool the activity type draws on.
function capPool(activityType: TravelActivityType, absenceMotivation: AbsenceMotivation) {
  if (absenceMotivation === 'WITH_PAPER_WITHIN_CAP') return 'withPaper';
  if (absenceMotivation === 'PROFESSIONAL_DEVELOPMENT_WITHIN_5') return 'professionalDevelopment';
  return travelFormationActivityTypes.includes(activityType) ? 'professionalDevelopment' : 'withPaper';
}

export function TravelAbsenceDayFields(props: TravelAbsenceDayFieldsProps) {
  const { control, isSubmitting, activityType, absenceMotivation, totals } = props;
  const { t } = useTranslation();
  const [vacationDays, unpaidDays, presentsPaper] = useWatch({
    control,
    name: ['absence.vacationDays', 'absence.unpaidDays', 'presentsPaper'],
  });
  const capDaysBlock = travelCapDaysBlockSubmit(
    totals?.enforcement.dayCap,
    absenceMotivation,
    activityType,
    presentsPaper,
  );
  const sum = totals == null ? null : travelDaySum(totals, absenceMotivation, vacationDays, unpaidDays, capDaysBlock);
  // Days nobody can say the cap pays are not shown as cap days.
  const capKnown = sum?.capUsableDays != null;
  // No leave fixes a motivation the activity type refuses, which the select already explains.
  const refused = totals != null && !totals.allowedMotivations.includes(absenceMotivation);
  const problem = sum?.problem === 'capDaysAboveUsable' && refused ? null : (sum?.problem ?? null);
  // Under WARN the backend accepts the request and only flags it.
  let blocks = true;
  if (problem === 'capDaysAboveUsable') {
    blocks = capDaysBlock;
  } else if (problem === 'leaveDaysExceedWorkingDays') {
    blocks = totals?.enforcement.leaveDays === 'BLOCK';
  }
  const problemColor = blocks ? 'red' : 'orange';
  // The cap never pays more than it has left; under WARN the backend still charges every uncovered day, so nothing is cut.
  const uncoveredDays = sum != null && capKnown && capDaysBlock ? sum.missingLeaveDays : 0;
  const shownCapDays = sum == null ? 0 : sum.capDays - uncoveredDays;

  return (
    <Stack gap={4}>
      <Group align="flex-end">
        <NumberInput
          label={t(`travel.requests.form.fields.capDays.${capPool(activityType, absenceMotivation)}`)}
          description={
            sum?.capUsableDays == null
              ? undefined
              : t('travel.requests.form.daySum.capUsable', { usable: sum.capUsableDays })
          }
          value={sum != null && capKnown ? shownCapDays : ''}
          error={problem === 'capDaysAboveUsable'}
          disabled
          readOnly
          flex={1}
        />
        <NumberInputControl
          name="absence.vacationDays"
          control={control}
          label={t('travel.requests.form.fields.vacationDays')}
          isSubmitting={isSubmitting}
          min={0}
        />
        <NumberInputControl
          name="absence.unpaidDays"
          control={control}
          label={t('travel.requests.form.fields.unpaidDays')}
          isSubmitting={isSubmitting}
          min={0}
        />
      </Group>
      {sum != null && (capKnown || problem === 'leaveDaysExceedWorkingDays') && (
        <Text
          size="sm"
          c={problem == null && uncoveredDays === 0 ? 'dimmed' : problemColor}
        >
          {t(`travel.requests.form.daySum.${problem === 'leaveDaysExceedWorkingDays' ? 'lineExceeds' : 'line'}`, {
            workingDays: sum.workingDays,
            capDays: shownCapDays,
            vacationDays: sum.vacationDays,
            unpaidDays: sum.unpaidDays,
          })}
          {uncoveredDays > 0 && `; ${t('travel.requests.form.daySum.uncovered', { count: uncoveredDays })}`}
        </Text>
      )}
      {sum != null && problem != null && (
        <Text
          size="sm"
          c={problemColor}
          role="alert"
        >
          {t(`travel.requests.form.daySum.${problem}`, {
            usable: sum.capUsableDays ?? 0,
            missing: sum.missingLeaveDays,
            leaveDays: sum.vacationDays + sum.unpaidDays,
            workingDays: sum.workingDays,
          })}{' '}
          {t(`travel.requests.form.daySum.${blocks ? 'blocksSubmit' : 'flagsRequest'}`)}
        </Text>
      )}
    </Stack>
  );
}
